import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowIcon,
  BottomContent,
  BottomTag,
  Content,
  Div,
  Dot,
  DotIcon,
  FirstHistory,
  LeftContent,
  Line,
  ModalBox,
  RightContent,
  SubHistory,
  Subtext,
  Text
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import Button from '../../button';
import { DocVersion, DocVersionList } from '../../../interfaces/ProjectInterface';
import dayjs from 'dayjs';
import {
  BUTTON_TYPE,
  COLOR_THEME_TYPE,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE
} from '../../../global/constants';
import { getDocVersion, getDocVersionList } from '../../../services/docServices';
import { REACT_APP_BLOCKSUITE_URL } from '../../../global/environment';
import { dispatchProjectSuccess } from '../../../services/projectServices';
import { DocData } from '../../../mobx/interfaces/project';
import IconButton from '../../button/iconButton';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../emptyState';
import ModalHeader from '../modalHeader';

interface Props {
  onClose: () => void;
  docUrl: string;
  docData: DocData | null;
  mainIframe: RefObject<HTMLIFrameElement>;
}

interface HistoryItem {
  date: string;
  times: Version[];
}
interface Version {
  time: string;
  id: number;
}

interface MessageEventData {
  type: string;
}

const versionsCache = new Map<number, string>();

function VersionHistoryModal(props: Props) {
  const { onClose, mainIframe, docData, docUrl } = props;
  //Refs
  const iframeRef = useRef<HTMLIFrameElement>(null);
  //States
  const [isEmptyStateVisible, setIsEmptyStateVisible] = useState(false);
  const [isHistoryShow, setIsHistoryShow] = useState<{ [key: string]: boolean }>({});
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [activeVersion, setActiveVersion] = useState<Version>({ id: -1, time: '' });
  const [loading, setLoading] = useState(false);
  // mobx state variable
  const { appStore, workspaceStore } = rootStore;
  const { themeMode } = appStore || {};
  const { currentWorkspace } = workspaceStore;
  const workspace_id = currentWorkspace?.id;
  //Other variable
  const isDarkMode = themeMode.theme === COLOR_THEME_TYPE.DARK;

  useEffect(() => {
    const lodDocVersions = async () => {
      const docVersions = (await getDocVersionList(docData?.id)) as DocVersionList;

      if (docVersions.length <= 0) {
        setIsEmptyStateVisible(true);
        return;
      }

      const formattedDocVersions = formatDocVersions(docVersions);

      setHistoryData(formattedDocVersions);
      setActiveVersion(formattedDocVersions[0].times[0]);

      const docVersion = (await getDocVersion(formattedDocVersions[0].times[0].id)) as DocVersion;

      iframeRef.current?.contentWindow?.postMessage(
        { type: 'update_content', payload: docVersion.Content },
        `${REACT_APP_BLOCKSUITE_URL}`
      );
    };

    const listner = (ev: MessageEvent<MessageEventData>) => {
      const { type } = ev.data;
      if (type === 'history-restore-complete') {
        setTimeout(() => {
          setLoading(false);
          dispatchProjectSuccess('Document Restored Successfully');
        }, 300);
      }
    };

    window.addEventListener('message', listner);
    lodDocVersions();

    return () => window.removeEventListener('message', listner);
  }, [docData?.id]);

  const formatDocVersions = (docVersions: DocVersionList) => {
    const dateMap = new Map<string, HistoryItem>();

    for (const version of docVersions) {
      const today = new Date();
      const versionDate = new Date(version.createdAt);

      const isToday = today.toDateString() === versionDate.toDateString();

      const date = isToday ? 'Today' : dayjs(versionDate).format('ddd, DD MMM YYYY');
      const time = dayjs(versionDate).format('h:mm a');

      const timeItem = { time, id: version.id };

      if (dateMap.has(date)) {
        const historyItem = dateMap.get(date);
        if (historyItem)
          dateMap.set(date, {
            ...historyItem,
            times: [...(historyItem?.times || []), timeItem]
          });
      } else {
        dateMap.set(date, { date, times: [timeItem] });
      }
    }

    return Array.from(dateMap.values());
  };

  const handleHistoryClick = (section: string) => {
    setIsHistoryShow((prevSections) => ({
      ...prevSections,
      [section]: !prevSections[section]
    }));
  };

  const handleVersionClick = async (version: Version) => {
    setActiveVersion(version);
  };

  const fetchAndPostActiveVersion = useCallback(async (versionId: number) => {
    let content: string;
    if (versionsCache.has(versionId)) {
      content = versionsCache.get(versionId) || '';
    } else {
      const docVersion = (await getDocVersion(versionId)) as DocVersion;
      content = docVersion.Content;
      versionsCache.set(versionId, content);
    }
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'update_content', payload: content },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  }, []);

  const updateContent = () => {
    setLoading(true);
    mainIframe.current?.contentWindow?.postMessage(
      { type: 'request_version_restore', payload: activeVersion.id },
      `${REACT_APP_BLOCKSUITE_URL}`
    );

    if (mainIframe.current) {
      mainIframe.current.onload = () => {
        setLoading(false);
        onClose();
      };
    }
  };

  const docIframe = useMemo(
    () => (
      <iframe
        key={activeVersion.id}
        ref={iframeRef}
        src={`${docUrl}/viewer?workspace_id=${workspace_id}&theme=${isDarkMode ? 'dark' : 'light'}&isEdge=${
          docData?.isEdge
        }`}
        height={500}
        width={'100%'}
        title='teamcamp doc'
        className='docs-section'
        allow='clipboard-read; clipboard-write'
        onLoad={() => activeVersion.id !== -1 && fetchAndPostActiveVersion(activeVersion.id)}
      />
    ),
    [activeVersion.id, fetchAndPostActiveVersion, isDarkMode, docData?.isEdge, docUrl, workspace_id]
  );

  return (
    <ModalBox>
      <IconButton
        iconName='new-close-icon'
        size={ICON_BUTTON_SIZE.SMALL}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        onClick={onClose}
        className='close-icon-sticky'
      />
      <Content>
        <LeftContent>{docIframe}</LeftContent>
        <RightContent>
          <ModalHeader title='Version History' />
          <BottomContent>
            {historyData?.map((history) => (
              <React.Fragment key={history?.date}>
                <FirstHistory role='button' onClick={() => handleHistoryClick(history?.date)}>
                  <ArrowIcon>
                    <SVGIcon
                      name='updated-right-arrow-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='var(--text-primary)'
                      className={isHistoryShow[history?.date] ? 'rotated ' : ''}
                    />
                  </ArrowIcon>
                  <Text>{history?.date}</Text>
                </FirstHistory>
                {isHistoryShow[history?.date] && (
                  <Div>
                    {history?.times.map((time) => (
                      <SubHistory
                        role='button'
                        key={time?.id}
                        onClick={() => handleVersionClick(time)}
                        isActive={time?.id === activeVersion?.id}>
                        <DotIcon>
                          <Line />
                          <Dot isActive={time?.id === activeVersion?.id} />
                        </DotIcon>
                        <Subtext>{time?.time}</Subtext>
                      </SubHistory>
                    ))}
                  </Div>
                )}
              </React.Fragment>
            ))}
          </BottomContent>
          <BottomTag>
            <Button
              title='Restore Current Version'
              variant={BUTTON_TYPE.PRIMARY}
              isLoading={loading}
              onClick={updateContent}
            />
          </BottomTag>
        </RightContent>
      </Content>
      {/* Emptystate ui */}
      {isEmptyStateVisible && (
        <EmptyStateV2
          size={EMPTY_STATE_SIZE.MEDIUM}
          title='No document history yet'
          description='Your document history is currently empty.'
          imageUrl='version_history_empty'
          isAlignedCenter={true}
        />
      )}
    </ModalBox>
  );
}

export default observer(VersionHistoryModal);
